import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { Car, FileSpreadsheet, Package, Search } from "lucide-react";
import { Badge } from "../../components/ui/badge";
import { Button } from "../../components/ui/button";
import { Card, CardContent } from "../../components/ui/card";
import { Checkbox } from "../../components/ui/checkbox";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../../components/ui/table";
import { Tabs, TabsList, TabsTrigger } from "../../components/ui/tabs";
import { useApiMessages } from "../../hooks/useApiMessages";
import { useVisitorScope } from "../../components/visitors/useVisitorScope";
import { PageHeader, ScopeFilters, StatusBadge } from "../../components/visitors/VisitorShared";
import { VisitDetailDialog } from "../../components/visitors/VisitDetailDialog";
import { dayRangeIso, exportToExcel, fmtDateTime, todayInput, visitorApi } from "../../components/visitors/visitorUtils";

export default function VehiclesEquipment() {
  const { t } = useTranslation();
  const { getErrorMessage } = useApiMessages();
  const scope = useVisitorScope();
  const [tab, setTab] = useState("vehicles");
  const [visits, setVisits] = useState([]);
  const [from, setFrom] = useState(todayInput());
  const [to, setTo] = useState(todayInput());
  const [search, setSearch] = useState("");
  const [pendingOnly, setPendingOnly] = useState(false);
  const [detailId, setDetailId] = useState(null);

  const load = useCallback(async () => {
    try {
      setVisits(
        await visitorApi.get("/visits", {
          ...scope.params,
          ...dayRangeIso(from, to),
          search: tab === "vehicles" ? search : undefined,
          with_vehicle: tab === "vehicles" || undefined,
          with_items: tab === "items" || undefined,
          limit: 1000,
        })
      );
    } catch (error) {
      toast.error(getErrorMessage(error, "visitors.errors.loadError"));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scope.companyId, scope.facilityId, from, to, tab, search]);

  useEffect(() => {
    const handle = setTimeout(load, 300);
    return () => clearTimeout(handle);
  }, [load]);

  // Un renglón por ítem registrado
  const itemRows = useMemo(() => {
    if (tab !== "items") return [];
    const q = search.trim().toLowerCase();
    return visits
      .flatMap((v) => (v.items || []).map((i) => ({ ...i, visit: v })))
      .filter((r) => !pendingOnly || (r.direction === "in" && !r.checked_out))
      .filter((r) => !q || [r.description, r.serial, r.visit.visitor_name].filter(Boolean).some((s) => s.toLowerCase().includes(q)))
      .sort((a, b) => (a.recorded_at < b.recorded_at ? 1 : -1));
  }, [visits, tab, pendingOnly, search]);

  const exportCurrent = () => {
    if (tab === "vehicles") {
      exportToExcel(
        visits,
        [
          { header: t("visitors.common.plate"), value: (v) => v.vehicle?.plate },
          { header: t("visitors.common.make"), value: (v) => v.vehicle?.make },
          { header: t("visitors.common.model"), value: (v) => v.vehicle?.model },
          { header: t("visitors.common.color"), value: (v) => v.vehicle?.color },
          { header: t("visitors.common.visitor"), value: (v) => v.visitor_name, width: 26 },
          { header: t("visitors.common.facility"), value: (v) => v.facility_name, width: 22 },
          { header: t("visitors.common.checkIn"), value: (v) => fmtDateTime(v.check_in_at) },
          { header: t("visitors.common.checkOut"), value: (v) => fmtDateTime(v.check_out_at) },
        ],
        `vehiculos_${from}_${to}`,
        `${t("visitors.vehicles.vehiclesTab")} (${from} – ${to})`
      );
    } else {
      exportToExcel(
        itemRows,
        [
          { header: t("visitors.common.item"), value: (r) => r.description, width: 26 },
          { header: t("visitors.common.category"), value: (r) => t(`visitors.itemCategories.${r.category || "other"}`) },
          { header: t("visitors.common.serial"), value: (r) => r.serial },
          { header: t("visitors.common.quantity"), value: (r) => r.quantity, width: 10 },
          { header: t("visitors.vehicles.direction"), value: (r) => t(`visitors.vehicles.${r.direction}`) },
          { header: t("visitors.common.visitor"), value: (r) => r.visit.visitor_name, width: 26 },
          { header: t("visitors.common.facility"), value: (r) => r.visit.facility_name, width: 22 },
          { header: t("visitors.common.date"), value: (r) => fmtDateTime(r.recorded_at) },
          { header: t("visitors.common.guard"), value: (r) => r.recorded_by_name },
          {
            header: t("visitors.common.status"),
            value: (r) => (r.direction === "in" ? t(r.checked_out ? "visitors.vehicles.checkedOut" : "visitors.vehicles.pending") : ""),
          },
        ],
        `equipos_${from}_${to}`,
        `${t("visitors.vehicles.itemsTab")} (${from} – ${to})`
      );
    }
  };

  const hasRows = tab === "vehicles" ? visits.length > 0 : itemRows.length > 0;

  return (
    <div className="space-y-6">
      <PageHeader
        title={t("visitors.vehicles.title")}
        subtitle={t("visitors.vehicles.subtitle")}
        actions={
          <Button variant="outline" disabled={!hasRows} onClick={exportCurrent}>
            <FileSpreadsheet className="h-4 w-4 mr-2" />
            Excel
          </Button>
        }
      />

      <ScopeFilters scope={scope}>
        <div>
          <Label className="text-sm text-slate-600 mb-1 block">{t("visitors.common.dateFrom")}</Label>
          <Input type="date" value={from} onChange={(e) => setFrom(e.target.value)} />
        </div>
        <div>
          <Label className="text-sm text-slate-600 mb-1 block">{t("visitors.common.dateTo")}</Label>
          <Input type="date" value={to} onChange={(e) => setTo(e.target.value)} />
        </div>
      </ScopeFilters>

      <div className="flex flex-col sm:flex-row sm:items-center gap-3 justify-between">
        <Tabs value={tab} onValueChange={setTab}>
          <TabsList>
            <TabsTrigger value="vehicles">
              <Car className="h-4 w-4 mr-2" />
              {t("visitors.vehicles.vehiclesTab")}
            </TabsTrigger>
            <TabsTrigger value="items">
              <Package className="h-4 w-4 mr-2" />
              {t("visitors.vehicles.itemsTab")}
            </TabsTrigger>
          </TabsList>
        </Tabs>
        <div className="flex items-center gap-3">
          {tab === "items" && (
            <label className="flex items-center gap-2 cursor-pointer text-sm whitespace-nowrap">
              <Checkbox checked={pendingOnly} onCheckedChange={(c) => setPendingOnly(!!c)} />
              {t("visitors.vehicles.pendingOnly")}
            </label>
          )}
          <div className="relative w-full sm:w-64">
            <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input className="pl-9" placeholder={t("visitors.common.search")} value={search} onChange={(e) => setSearch(e.target.value)} />
          </div>
        </div>
      </div>

      <Card>
        <CardContent className="p-0 sm:p-2">
          {!hasRows ? (
            <p className="text-center py-10 text-slate-500">{t("visitors.common.noResults")}</p>
          ) : tab === "vehicles" ? (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>{t("visitors.common.plate")}</TableHead>
                  <TableHead className="hidden md:table-cell">{t("visitors.vehicles.vehicleDesc")}</TableHead>
                  <TableHead>{t("visitors.common.visitor")}</TableHead>
                  <TableHead className="hidden sm:table-cell">{t("visitors.common.checkIn")}</TableHead>
                  <TableHead className="hidden sm:table-cell">{t("visitors.common.checkOut")}</TableHead>
                  <TableHead>{t("visitors.common.status")}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {visits.map((v) => (
                  <TableRow key={v.id} className="cursor-pointer" onClick={() => setDetailId(v.id)}>
                    <TableCell className="font-mono font-semibold">{v.vehicle?.plate}</TableCell>
                    <TableCell className="hidden md:table-cell">
                      {[v.vehicle?.make, v.vehicle?.model, v.vehicle?.color].filter(Boolean).join(" · ") || "—"}
                    </TableCell>
                    <TableCell>
                      <p className="font-medium">{v.visitor_name}</p>
                      <p className="text-xs text-slate-500">{v.facility_name}</p>
                    </TableCell>
                    <TableCell className="hidden sm:table-cell text-sm">{fmtDateTime(v.check_in_at)}</TableCell>
                    <TableCell className="hidden sm:table-cell text-sm">{fmtDateTime(v.check_out_at)}</TableCell>
                    <TableCell>
                      <StatusBadge status={v.status} />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>{t("visitors.common.item")}</TableHead>
                  <TableHead className="hidden md:table-cell">{t("visitors.common.serial")}</TableHead>
                  <TableHead>{t("visitors.vehicles.direction")}</TableHead>
                  <TableHead>{t("visitors.common.visitor")}</TableHead>
                  <TableHead className="hidden sm:table-cell">{t("visitors.common.date")}</TableHead>
                  <TableHead>{t("visitors.common.status")}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {itemRows.map((r) => (
                  <TableRow key={r.id} className="cursor-pointer" onClick={() => setDetailId(r.visit.id)}>
                    <TableCell>
                      <p className="font-medium">
                        {r.quantity > 1 ? `${r.quantity} × ` : ""}
                        {r.description}
                      </p>
                      <p className="text-xs text-slate-500">{t(`visitors.itemCategories.${r.category || "other"}`)}</p>
                    </TableCell>
                    <TableCell className="hidden md:table-cell font-mono text-sm">{r.serial || "—"}</TableCell>
                    <TableCell>
                      <Badge variant="outline">{t(`visitors.vehicles.${r.direction}`)}</Badge>
                    </TableCell>
                    <TableCell>
                      <p className="text-sm">{r.visit.visitor_name}</p>
                      <p className="text-xs text-slate-500">{r.visit.facility_name}</p>
                    </TableCell>
                    <TableCell className="hidden sm:table-cell text-sm">{fmtDateTime(r.recorded_at)}</TableCell>
                    <TableCell>
                      {r.direction === "in" &&
                        (r.checked_out ? (
                          <Badge variant="secondary">{t("visitors.vehicles.checkedOut")}</Badge>
                        ) : (
                          <Badge variant="outline" className="border-amber-300 text-amber-700">
                            {t("visitors.vehicles.pending")}
                          </Badge>
                        ))}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <VisitDetailDialog
        visitId={detailId}
        open={!!detailId}
        onOpenChange={(o) => !o && setDetailId(null)}
        scope={scope}
        onChanged={load}
      />
    </div>
  );
}
